import { useState, ReactNode, ComponentType, FC } from 'react';
import {
  LayoutDashboard,
  Tag,
  Clock,
  Users,
  Link2,
  Settings,
  Menu,
  X,
  ChevronLeft,
  LogOut,
  Sun,
  Moon,
  Zap,
  Smartphone,
  Play,
  Pause,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Tooltip } from '../ui/Tooltip';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';

interface SubNavItem {
  id: string;
  label: string;
  path: string;
}

interface NavItem {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  badge?: string;
  disabled?: boolean;
  subItems?: SubNavItem[];
}

const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'whatsapp', label: 'Provedor WhatsApp', icon: Smartphone },
  {
    id: 'grupos',
    label: 'Grupos',
    icon: Users,
    subItems: [
      { id: 'grupos', label: 'Grupos VIP', path: 'grupos' },
      {
        id: 'grupos/monitor',
        label: 'Monitor de Evasões',
        path: 'grupos/monitor',
      },
      {
        id: 'grupos/espelho',
        label: 'Espelho Automático',
        path: 'grupos/espelho',
      },
    ],
  },
  {
    id: 'ofertas',
    label: 'Ofertas',
    icon: Tag,
    subItems: [
      { id: 'ofertas', label: 'Minhas Ofertas', path: 'ofertas' },
      {
        id: 'ofertas/cupons',
        label: 'Catálogo de Cupons',
        path: 'ofertas/cupons',
      },
    ],
  },
  { id: 'fila', label: 'Fila de Disparo', icon: Clock },
  {
    id: 'bridge',
    label: 'Páginas Ponte',
    icon: Link2,
  },
  { id: 'settings', label: 'Configurações', icon: Settings },
];

import { HealthStatus } from '../../utils/operationalHealth';

interface AppShellProps {
  children: ReactNode;
  activeTab: string;
  onTabChange: (id: string) => void;
  whatsappConnected?: boolean;
  whatsappPhone?: string | null;
  activeProjectName?: string;
  whatsappHealth?: HealthStatus;
  groupsHealth?: HealthStatus;
  queueHealth?: HealthStatus;
  globalHealth?: HealthStatus;
  globalTooltip?: string;
  isQueuePaused?: boolean;
  onToggleQueuePause?: () => void;
  isTogglingQueue?: boolean;
  canToggleQueue?: boolean;
  queueBlockedReason?: string;
}

export const AppShell: FC<AppShellProps> = ({
  children,
  activeTab,
  onTabChange,
  activeProjectName = 'Achadinhos Tech',
  whatsappHealth = 'neutral',
  groupsHealth = 'neutral',
  queueHealth = 'neutral',
  globalHealth = 'neutral',
  globalTooltip,
  isQueuePaused = false,
  onToggleQueuePause,
  isTogglingQueue = false,
  canToggleQueue = false,
  queueBlockedReason,
}) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);

  const handleQueueActionClick = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!onToggleQueuePause || !canToggleQueue || isTogglingQueue) return;
    if (isQueuePaused) {
      // Retomar imediatamente sem modal
      onToggleQueuePause();
    } else {
      // Para pausar, confirma com o usuário
      setIsPauseModalOpen(true);
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-surface-canvas text-content-main transition-colors duration-150">
      {/* Mobile Drawer Backdrop */}
      {isMobileDrawerOpen && (
        <div
          className="backdrop-blur-xs fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden"
          onClick={() => setIsMobileDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-surface-border bg-surface-panel transition-all duration-200 ease-in-out lg:static ${isSidebarCollapsed ? 'w-16' : 'w-56'} ${isMobileDrawerOpen ? 'w-56 translate-x-0' : '-translate-x-full lg:translate-x-0'} `}
      >
        {/* Brand Header - Raio do meio em branco e ao redor verde */}
        <div
          className={`flex h-14 items-center border-b border-surface-border px-3.5 ${
            isSidebarCollapsed ? 'justify-center' : 'justify-between'
          }`}
        >
          <div
            className={`flex items-center gap-2.5 overflow-hidden ${
              isSidebarCollapsed ? 'cursor-pointer' : ''
            }`}
            onClick={
              isSidebarCollapsed
                ? () => setIsSidebarCollapsed(false)
                : undefined
            }
            title={isSidebarCollapsed ? 'Expandir menu' : undefined}
          >
            <div className="shadow-xs flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-brand-500 font-extrabold shadow-brand-500/20">
              <Zap className="h-4 w-4 fill-white stroke-white" />
            </div>
            {(!isSidebarCollapsed || isMobileDrawerOpen) && (
              <span className="block truncate text-sm font-extrabold tracking-tight text-content-main">
                TrafficForge
              </span>
            )}
          </div>

          <button
            onClick={() => setIsMobileDrawerOpen(false)}
            aria-label="Fechar menu"
            className="cursor-pointer rounded-lg p-1 text-content-muted hover:text-content-main lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>

          {!isSidebarCollapsed && (
            <button
              onClick={() => setIsSidebarCollapsed(true)}
              aria-label="Recolher menu"
              className="hidden cursor-pointer rounded-lg p-1 text-content-muted transition-colors hover:bg-surface-subtle hover:text-content-main lg:flex"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Project Selector Badge com semáforo agregado de prontidão */}
        {!isSidebarCollapsed || isMobileDrawerOpen ? (
          <div className="relative z-30 w-full px-2 pt-2.5">
            <Tooltip
              content={globalTooltip || 'Status da Operação'}
              position="bottom"
              className="block w-full"
            >
              <div className="hover:bg-surface-border/40 flex w-full cursor-help items-center justify-between gap-2 rounded-lg border border-surface-border bg-surface-subtle px-3 py-2 text-xs transition-colors">
                <span className="truncate text-xs font-semibold text-content-main">
                  {activeProjectName}
                </span>

                <span
                  data-testid="health-dot-global"
                  className={`h-2 w-2 flex-shrink-0 rounded-full ${
                    globalHealth === 'online'
                      ? 'shadow-xs bg-brand-500 shadow-brand-500/50'
                      : globalHealth === 'warning'
                        ? 'bg-amber-500'
                        : globalHealth === 'danger'
                          ? 'bg-red-500'
                          : 'bg-slate-400 dark:bg-slate-600'
                  }`}
                />
              </div>
            </Tooltip>
          </div>
        ) : (
          <div className="relative z-30 flex w-full justify-center pt-2.5">
            <Tooltip
              content={`${activeProjectName} - ${globalTooltip || 'Status da Operação'}`}
              position="right"
            >
              <span
                data-testid="health-dot-global"
                className={`h-2.5 w-2.5 rounded-full ${
                  globalHealth === 'online'
                    ? 'shadow-xs bg-brand-500 shadow-brand-500/50'
                    : globalHealth === 'warning'
                      ? 'bg-amber-500'
                      : globalHealth === 'danger'
                        ? 'bg-red-500'
                        : 'bg-slate-400 dark:bg-slate-600'
                }`}
              />
            </Tooltip>
          </div>
        )}

        {/* Navigation List */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const hasSubItems = Boolean(
              item.subItems && item.subItems.length > 0
            );
            const isParentActive =
              activeTab === item.id ||
              activeTab.startsWith(`${item.id}/`) ||
              (item.id === 'grupos' &&
                (activeTab === 'groups' || activeTab.startsWith('groups/'))) ||
              (item.id === 'ofertas' &&
                (activeTab === 'offers' || activeTab.startsWith('offers/'))) ||
              (item.id === 'fila' &&
                (activeTab === 'campaigns' ||
                  activeTab.startsWith('campaigns/')));
            const isDisabled = item.disabled;

            // Determina semáforo específico
            let itemHealth: HealthStatus | undefined = undefined;
            if (item.id === 'whatsapp') itemHealth = whatsappHealth;
            if (item.id === 'grupos') itemHealth = groupsHealth;
            if (item.id === 'fila') itemHealth = queueHealth;

            return (
              <div key={item.id} className="space-y-0.5">
                <button
                  onClick={() => {
                    if (isDisabled) return;
                    onTabChange(item.id);
                    setIsMobileDrawerOpen(false);
                  }}
                  title={
                    isDisabled
                      ? `${item.label} (Em breve)`
                      : isSidebarCollapsed
                        ? item.label
                        : undefined
                  }
                  className={`relative flex w-full items-center rounded-lg text-xs font-medium transition-colors ${
                    isSidebarCollapsed
                      ? 'justify-center p-2.5'
                      : 'gap-2.5 px-3 py-2'
                  } ${
                    isDisabled
                      ? 'cursor-not-allowed opacity-50'
                      : isParentActive
                        ? hasSubItems
                          ? 'cursor-pointer font-semibold text-content-main'
                          : 'cursor-pointer bg-surface-subtle font-semibold text-content-main'
                        : 'hover:bg-surface-subtle/60 cursor-pointer text-content-muted hover:text-content-main'
                  } `}
                >
                  <div className="relative flex items-center justify-center">
                    <Icon
                      className={`h-4 w-4 flex-shrink-0 ${
                        isParentActive
                          ? 'text-content-main'
                          : 'text-content-muted'
                      }`}
                    />
                    {/* Quando a barra estiver colapsada, exibe o semáforo sobre o ícone */}
                    {isSidebarCollapsed && itemHealth && (
                      <span
                        data-testid={`health-dot-${item.id}`}
                        className={`absolute -right-1 -top-1 h-2 w-2 rounded-full ring-2 ring-surface-panel ${
                          itemHealth === 'online'
                            ? 'bg-brand-500'
                            : itemHealth === 'warning'
                              ? 'bg-amber-500'
                              : itemHealth === 'danger'
                                ? 'bg-red-500'
                                : 'bg-slate-400 dark:bg-slate-600'
                        }`}
                      />
                    )}
                  </div>

                  {(!isSidebarCollapsed || isMobileDrawerOpen) && (
                    <span className="truncate">{item.label}</span>
                  )}

                  {/* Botão de Pausa e Semáforo da Fila quando Expandida */}
                  {(!isSidebarCollapsed || isMobileDrawerOpen) && (
                    <div className="ml-auto flex items-center gap-1.5">
                      {item.id === 'fila' && onToggleQueuePause && (
                        <span
                          onClick={
                            canToggleQueue ? handleQueueActionClick : undefined
                          }
                          title={
                            !canToggleQueue
                              ? queueBlockedReason || 'Fila Indisponível'
                              : isQueuePaused
                                ? 'Retomar Fila'
                                : 'Pausar Fila'
                          }
                          className={`flex h-5 w-5 items-center justify-center rounded-md border border-surface-border transition-colors ${
                            !canToggleQueue
                              ? 'bg-surface-subtle/50 text-content-muted/40 cursor-not-allowed'
                              : 'cursor-pointer bg-surface-subtle text-content-muted hover:bg-surface-panel hover:text-content-main'
                          }`}
                        >
                          {isQueuePaused ? (
                            <Play className="h-2.5 w-2.5 fill-current" />
                          ) : (
                            <Pause className="h-2.5 w-2.5 fill-current" />
                          )}
                        </span>
                      )}

                      {itemHealth && (
                        <span
                          data-testid={`health-dot-${item.id}`}
                          className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                            itemHealth === 'online'
                              ? 'bg-brand-500'
                              : itemHealth === 'warning'
                                ? 'bg-amber-500'
                                : itemHealth === 'danger'
                                  ? 'bg-red-500'
                                  : 'bg-slate-400 dark:bg-slate-600'
                          }`}
                        />
                      )}

                      {item.badge && !itemHealth && (
                        <span className="rounded-md border border-surface-border bg-surface-subtle px-1.5 py-0.5 font-mono text-micro text-content-muted">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  )}
                </button>

                {/* Submenu aninhado quando expandido e ativo */}
                {item.subItems &&
                  isParentActive &&
                  (!isSidebarCollapsed || isMobileDrawerOpen) && (
                    <div className="ml-7 space-y-0.5 border-l border-surface-border py-0.5 pl-2">
                      {item.subItems.map((sub) => {
                        const isSubActive =
                          activeTab === sub.path ||
                          (sub.path === item.id && activeTab === item.id);

                        return (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onTabChange(sub.path);
                              setIsMobileDrawerOpen(false);
                            }}
                            className={`flex w-full cursor-pointer items-center justify-between whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                              isSubActive
                                ? 'shadow-xs border border-brand-500/30 bg-brand-500/10 font-bold text-brand-600 dark:text-brand-400'
                                : 'hover:bg-surface-subtle/50 text-content-muted hover:text-content-main'
                            }`}
                          >
                            <span>{sub.label}</span>
                            {isSubActive && (
                              <span className="shadow-xs h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500 shadow-brand-500/50" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
              </div>
            );
          })}
        </nav>

        {/* Theme switcher */}
        <div className="flex items-center justify-between border-t border-surface-border p-2">
          <Tooltip
            content={
              isSidebarCollapsed
                ? theme === 'dark'
                  ? 'Modo Claro'
                  : 'Modo Escuro'
                : undefined
            }
            position="right"
            className="w-full"
          >
            <button
              onClick={toggleTheme}
              aria-label={
                theme === 'dark' ? 'Ativar Modo Claro' : 'Ativar Modo Escuro'
              }
              className={`flex w-full cursor-pointer items-center rounded-lg text-xs text-content-muted transition-colors hover:bg-surface-subtle hover:text-content-main ${
                isSidebarCollapsed
                  ? 'justify-center p-2.5'
                  : 'gap-2.5 px-3 py-2'
              }`}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 flex-shrink-0 text-amber-400" />
              ) : (
                <Moon className="h-4 w-4 flex-shrink-0 text-slate-500" />
              )}
              {(!isSidebarCollapsed || isMobileDrawerOpen) && (
                <span className="truncate">
                  {theme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}
                </span>
              )}
            </button>
          </Tooltip>
        </div>
      </aside>

      {/* Main Area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header - Limpo e direto, sem o botão redundante de WhatsApp */}
        <header className="z-30 flex h-14 items-center justify-between border-b border-surface-border bg-surface-panel px-4 transition-colors sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileDrawerOpen(true)}
              aria-label="Abrir navegação"
              className="cursor-pointer rounded-lg p-1.5 text-content-muted transition-colors hover:bg-surface-subtle lg:hidden"
            >
              <Menu className="h-4 w-4" />
            </button>

            <h1 className="text-sm font-semibold text-content-main">
              {navItems.find((n) => n.id === activeTab)?.label || 'Painel'}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Controle Global da Fila de Disparos com Cores Neutras */}
            {onToggleQueuePause && (
              <Tooltip
                content={
                  !canToggleQueue
                    ? queueBlockedReason || 'Esteira indisponível no momento'
                    : isQueuePaused
                      ? 'Esteira pausada. Clique para retomar os disparos automáticos.'
                      : 'Esteira ativa. Clique para pausar os envios da fila.'
                }
              >
                <button
                  onClick={() => handleQueueActionClick()}
                  disabled={!canToggleQueue || isTogglingQueue}
                  aria-label={
                    isQueuePaused ? 'Retomar Esteira' : 'Pausar Esteira'
                  }
                  className={`flex items-center gap-1.5 rounded-lg border border-surface-border px-2.5 py-1 text-xs font-medium transition-colors ${
                    !canToggleQueue
                      ? 'bg-surface-subtle/50 text-content-muted/40 cursor-not-allowed'
                      : 'cursor-pointer bg-surface-subtle text-content-main hover:bg-surface-panel hover:text-content-main'
                  }`}
                >
                  {isQueuePaused ? (
                    <>
                      <Play className="h-3.5 w-3.5 fill-current text-content-muted" />
                      <span className="hidden sm:inline">Retomar Fila</span>
                    </>
                  ) : (
                    <>
                      <Pause className="h-3.5 w-3.5 fill-current text-content-muted" />
                      <span className="hidden sm:inline">Pausar Fila</span>
                    </>
                  )}
                </button>
              </Tooltip>
            )}

            {/* Perfil & Logout */}
            {user && (
              <div className="flex items-center gap-2">
                <span className="hidden max-w-28 truncate text-xs font-medium text-content-main md:inline">
                  {user.name}
                </span>
                <Tooltip content="Sair">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    aria-label="Sair"
                    className="p-1.5 text-content-muted hover:text-red-600 dark:hover:text-red-400"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </header>

        {/* Content Body */}
        <main className="flex-1 overflow-y-auto bg-surface-canvas p-4 transition-colors sm:p-6 lg:p-8">
          <div className="mx-auto max-w-5xl space-y-6">{children}</div>
        </main>
      </div>

      {/* Modal de Confirmação para Pausar a Esteira Global */}
      <ConfirmationModal
        isOpen={isPauseModalOpen}
        onClose={() => setIsPauseModalOpen(false)}
        onConfirm={async () => {
          setIsPauseModalOpen(false);
          if (onToggleQueuePause) onToggleQueuePause();
        }}
        title="Pausar Esteira de Disparos?"
        description="Ao pausar a esteira, todas as ofertas agendadas permanecerão retidas na fila. Novos disparos só serão enviados quando você retomar a esteira."
        confirmText="Pausar Fila"
        cancelText="Cancelar"
        variant="danger"
        isLoading={isTogglingQueue}
      />
    </div>
  );
};
